/**
 * Ablauf beim Schneiden: Schnittfolge des Messers und Zerlegen einer Zutat in
 * Stücke. Die Zutat wird dabei nicht durch ein neues Bild ersetzt, sondern
 * wirklich zerteilt – jedes Stück ist ein Ausschnitt der Zutat.
 */
import { ease, lerp, segment } from '../geometry.ts';
import { mixPose } from './pose.ts';
import type { Pose } from './pose.ts';
import { STROKE } from '../palette.ts';
import { roundedRectPath } from '../paths.ts';
import { group, rect, shape } from '../scene.ts';
import type { SceneNode } from '../scene.ts';
import { produceNodes } from './produce.ts';
import type { Produce } from './produce.ts';

/** Zustand innerhalb einer Schnittfolge. */
export interface CutState {
  /** Nummer des aktuellen Schnitts, 0 bis count - 1. */
  readonly index: number;
  /** Anheben des Messers, 1 oben, 0 unten auf dem Brett. */
  readonly lift: number;
  /** Fortschritt der Bewegung zur neuen Position, 0 bis 1. */
  readonly travel: number;
  /** Anzahl der fertigen Schnitte. */
  readonly done: number;
}

/**
 * Schnittfolge mit gleichmäßigem Takt: Messer zur nächsten Stelle führen,
 * absenken, kurz halten, anheben.
 */
export function cutSchedule(t: number, start: number, end: number, count: number): CutState {
  if (count <= 0) {
    return { index: 0, lift: 1, travel: 1, done: 0 };
  }
  const progress = segment(t, start, end) * count;
  if (progress >= count) {
    return { index: count - 1, lift: 1, travel: 1, done: count };
  }
  const index = Math.floor(progress);
  const local = progress - index;
  const travel = ease.inOutQuad(segment(local, 0, 0.35));
  const lift = local < 0.5 ? 1 - ease.inQuad(segment(local, 0.1, 0.5)) : ease.outQuad(segment(local, 0.6, 1));
  return { index, lift, travel, done: index + (local >= 0.5 ? 1 : 0) };
}

/** Rechteck eines Stücks im Raster. */
export interface Cell {
  readonly x0: number;
  readonly x1: number;
  readonly y0: number;
  readonly y1: number;
}

/** Lage eines Stücks: Versatz und ob es schon abgeschnitten ist (dann mit Schnittkanten). */
export type CellOffset = (
  column: number,
  row: number,
) => { readonly dx: number; readonly dy: number; readonly cut: boolean };

/**
 * Zeichnet eine Zutat als Raster aus Stücken.
 * @param xs Grenzen in x-Richtung, aufsteigend, einschließlich der Außenkanten.
 * @param ys Grenzen in y-Richtung, aufsteigend, einschließlich der Außenkanten.
 * @param offset Versatz je Stück; so rücken abgeschnittene Stücke auseinander.
 */
export function cellPieces(
  item: Produce,
  xs: readonly number[],
  ys: readonly number[],
  offset: CellOffset,
): SceneNode[] {
  const nodes: SceneNode[] = [];
  for (let column = 0; column < xs.length - 1; column += 1) {
    for (let row = 0; row < ys.length - 1; row += 1) {
      const cell: Cell = {
        x0: xs[column] ?? 0,
        x1: xs[column + 1] ?? 0,
        y0: ys[row] ?? 0,
        y1: ys[row + 1] ?? 0,
      };
      const { dx, dy, cut } = offset(column, row);
      nodes.push(piece(item, cell, dx, dy, cut));
    }
  }
  return nodes;
}

/**
 * Ein Stück: Ausschnitt der Zutat. Abgeschnittene Stücke bekommen ringsum
 * Schnittkanten; noch zusammenhängende Stücke fügen sich nahtlos aneinander.
 */
export function piece(item: Produce, cell: Cell, dx: number, dy: number, cut: boolean): SceneNode {
  const area = roundedRectPath(cell.x0, cell.y0, cell.x1 - cell.x0, cell.y1 - cell.y0, 0);
  const edges = cut
    ? group(
        [
          rect(cell.x0, cell.y0, cell.x1 - cell.x0, cell.y1 - cell.y0, 0, {
            stroke: 'ink',
            strokeWidth: STROKE.detail,
          }),
        ],
        { clip: item.outline },
      )
    : null;
  return group([group([...produceNodes(item), edges], { clip: area })], {
    transform: { x: dx, y: dy },
  });
}

/** Schnittlinie, die erscheint, sobald das Messer das Brett berührt. */
export function cutLine(x1: number, y1: number, x2: number, y2: number, clipPath: string): SceneNode {
  return group([shape(`M${x1} ${y1}L${x2} ${y2}`, { stroke: 'ink', strokeWidth: STROKE.detail })], {
    clip: clipPath,
  });
}

/** Gleichmäßig verteilte Grenzen zwischen a und b. */
export function divisions(a: number, b: number, parts: number): number[] {
  return Array.from({ length: parts + 1 }, (_, index) => lerp(a, b, index / parts));
}

/** Zeitplan einer Folge von Querschnitten. */
export interface CrossCutTiming {
  readonly approach: readonly [number, number];
  readonly cut: readonly [number, number];
  readonly park: readonly [number, number];
}

/**
 * Messerführung für Querschnitte: aus der Ruhelage zur ersten Schnittstelle,
 * dann Schnitt für Schnitt an den Stellen xs (Spitze nach oben, Griff zum
 * Koch), zurück in die Ruhelage.
 * @param y Lage der Schneide am Griff (unterhalb der Zutat).
 */
export function crossCutPose(
  t: number,
  xs: readonly number[],
  y: number,
  rest: Pose,
  timing: CrossCutTiming,
): Pose {
  const count = xs.length;
  const cutPose = (index: number, lift: number): Pose => ({ x: xs[index] ?? rest.x, y, rotate: 90, lift });
  if (count === 0) {
    return rest;
  }
  if (t < timing.cut[0]) {
    return mixPose(rest, cutPose(0, 1), ease.inOutCubic(segment(t, timing.approach[0], timing.approach[1])));
  }
  if (t < timing.park[0]) {
    const state = cutSchedule(t, timing.cut[0], timing.cut[1], count);
    const from = cutPose(Math.max(0, state.index - 1), 1);
    const to = cutPose(state.index, state.lift);
    return { ...to, x: lerp(from.x, to.x, state.travel) };
  }
  return mixPose(cutPose(count - 1, 1), rest, ease.inOutCubic(segment(t, timing.park[0], timing.park[1])));
}

/** Zeitpunkt, zu dem Querschnitt index von count fertig ist. */
export function crossCutDone(index: number, count: number, cut: readonly [number, number]): number {
  return lerp(cut[0], cut[1], (index + 0.5) / count);
}

/**
 * Sichtbare Klingenbreite: beim Schneiden steht die Klinge aufrecht und wirkt
 * von oben schmal, in der Ruhelage liegt sie flach.
 */
export function knifeSquash(t: number, timing: CrossCutTiming): number {
  const upright =
    ease.inOutCubic(segment(t, timing.approach[0], timing.approach[1])) -
    ease.inOutCubic(segment(t, timing.park[0], timing.park[1]));
  return lerp(1, 0.5, upright);
}
